import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listPutWalls, createPutWall, sendDivert, recentConveyorEvents } from "@/lib/hardware.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Mic, Boxes, Workflow } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/admin/hardware")({ component: HardwarePage });

function HardwarePage() {
  const { tenantId, facilityId } = useScope();
  const qc = useQueryClient();
  const lpw = useServerFn(listPutWalls);
  const cpw = useServerFn(createPutWall);
  const sd = useServerFn(sendDivert);
  const rce = useServerFn(recentConveyorEvents);

  const enabled = !!tenantId && !!facilityId;

  const { data: walls } = useQuery({
    queryKey: ["put-walls", tenantId, facilityId], enabled,
    queryFn: () => lpw({ data: { tenantId: tenantId!, facilityId: facilityId! } }),
  });
  const { data: events } = useQuery({
    queryKey: ["conveyor-events", tenantId, facilityId], enabled,
    queryFn: () => rce({ data: { tenantId: tenantId!, facilityId: facilityId!, limit: 25 } }),
    refetchInterval: 5_000,
  });

  const [code, setCode] = useState("");
  const [cells, setCells] = useState(16);
  const [target, setTarget] = useState("LANE-1");
  const [carton, setCarton] = useState("");

  const onAddWall = async () => {
    if (!enabled || !code) return;
    await runServerFn(() => cpw({ data: { tenantId: tenantId!, facilityId: facilityId!, code, cells } }), "Create put-wall");
    setCode("");
    qc.invalidateQueries({ queryKey: ["put-walls"] });
    toast.success("Put-wall added");
  };

  const onDivert = async () => {
    if (!enabled || !carton) return;
    await runServerFn(() => sd({ data: { tenantId: tenantId!, facilityId: facilityId!, divertTarget: target, cartonId: carton } }), "Divert carton");
    setCarton("");
    qc.invalidateQueries({ queryKey: ["conveyor-events"] });
    toast.success(`Diverted to ${target}`);
  };

  return (
    <>
      <PageHeader title="Hardware Adapters" description="Voice picking, put-walls (cluster picking), and conveyor diverter integration." />

      <div className="px-4 sm:px-6 py-4 grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Mic className="h-4 w-4" /> Voice picking</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p>Hands-free pick-by-voice via the operator app. Supports Honeywell Vocollect headsets and any Web Speech API device.</p>
            <Badge variant="outline">Adapter: Web Speech API + Vocollect VoiceClient</Badge>
            <p className="text-xs text-muted-foreground">Operators start sessions from <code>/app/picking</code> — every utterance is logged for QA.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Boxes className="h-4 w-4" /> Put-walls</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Wall code (PW-1)" value={code} onChange={(e) => setCode(e.target.value)} />
                <Input type="number" min={4} max={128} value={cells} onChange={(e) => setCells(Number(e.target.value) || 16)} />
              </div>
              <Button size="sm" onClick={onAddWall} disabled={!enabled}>Add put-wall</Button>
            </div>
            <ul className="text-xs space-y-1 border-t pt-2">
              {(walls?.walls ?? []).length === 0 ? (
                <li className="text-muted-foreground">No put-walls configured.</li>
              ) : walls!.walls.map((w: any) => (
                <li key={w.id} className="flex justify-between"><span className="font-mono">{w.code}</span><span className="text-muted-foreground">{w.cells} cells</span></li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Workflow className="h-4 w-4" /> Conveyor diverter</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="space-y-2">
              <Label className="text-xs">Send divert command</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Target (LANE-1)" value={target} onChange={(e) => setTarget(e.target.value.toUpperCase())} />
                <Input placeholder="Carton ID" value={carton} onChange={(e) => setCarton(e.target.value)} />
              </div>
              <Button size="sm" onClick={onDivert} disabled={!enabled || !carton}>Divert</Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="px-4 sm:px-6 pb-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Recent Conveyor Events</CardTitle></CardHeader>
          <CardContent className="p-0">
            {(events?.events ?? []).length === 0 ? (
              <div className="p-6 text-sm text-center text-muted-foreground">No conveyor activity.</div>
            ) : (
              <ul className="divide-y text-sm">
                {events!.events.map((e: any) => (
                  <li key={e.id} className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="font-mono">{e.divert_target}</Badge>
                      <span className="font-mono text-xs">{e.carton_id}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">{e.status}</Badge>
                      <span className="text-xs text-muted-foreground">{new Date(e.created_at).toLocaleTimeString()}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
